import { Component, output, input } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';

import { AvailableIcons, ApIconComponent } from '@ap/icon';

import { ButtonType, ButtonVariant } from './button.model';

/**
 * This is the Button Component. It renders a button with various options and styling
 **/
@Component({
    selector: 'ap-button',
    templateUrl: './button.component.html',
    styleUrls: ['./button.component.scss'],
    imports: [ReactiveFormsModule, NgClass, ApIconComponent]
})
export class ApButtonComponent {
    /**
     * Disable the button attribute preferably.
     */
    public readonly isDisabled = input(false);

    /**
     * Specify button type, defaults to `button`.
     * Options are `button` | `submit` | `reset`
     */
    public readonly type = input<ButtonType>('button');

    /**
     * Visual style of the button.
     * Options are `primary` (default), `secondary`, `edit` and `aside`.
     */
    public readonly variant = input<ButtonVariant>('primary');

    /**
     * removes margin around the button.
     */
    public readonly compact = input<boolean>(false);

    /**
     * sets the button to full-width, which can be required in combination with the grid.
     */
    public readonly wide = input<boolean>(false);

    /**
     * makes the button look like an anchor.
     */
    public readonly text = input<boolean>(false);

    /**
     * Used to define a string that labels the current element.
     * Use it in cases where a text label is not visible on the screen / there is no text in the button.
     */
    public readonly ariaLabel = input<string>(undefined);

    /**
     * Establishes relationships between objects and their label(s)
     * and its value should be one or more element IDs,
     * which refer to elements that have the text needed for labeling
     */
    public readonly ariaLabelledBy = input<string>(undefined);

    /**
     * adds a specified icon left of the button text (see ap-icon for icon options)
     */
    public readonly iconName = input<AvailableIcons>(undefined);

    /**
     * Emits event if button clicked
     */
    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    public readonly onClick = output<Event>();

    /**
     * on-click Callback function that gets called when the button is clicked. Use this instead of `ng-click`, because when `ng-click` is used the button can still be clicked (the click event still propagates) when the button is disabled.
     */
    protected buttonClicked(event: Event): void {
        if (!this.isDisabled()) {
            this.onClick.emit(event);
        }
    }
}
